import { useState, useEffect, useRef } from 'react';
import { Flex, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import type { FilterState } from '@/features/filterSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useDebouncedSpanCount } from './useDebouncedSpanCount';
import { capAndFormatNumber } from '@/helpers/numbers';

interface UseSpanCountWithDisplayProps {
  filterState: FilterState | null;
  projectId?: string | null;
}

export function SpanCountWithDisplay({
  filterState,
  projectId = undefined,
}: UseSpanCountWithDisplayProps) {
  const intl = useClientTerminology();

  // Animated display state for filtered count
  const [displayedCount, setDisplayedCount] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  // Get debounced query result directly
  const { data: spanCounts, isFetching } = useDebouncedSpanCount({
    filterState,
    projectId,
  });

  useEffect(() => {
    const start = displayedCount;
    const end = Math.max(0, spanCounts?.filtered || 0);

    if (start === end) {
      return;
    }

    // Clear any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const duration = 50;
    const startTime = performance.now();

    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const current = Math.max(0, Math.round(start + (end - start) * progress));

      setDisplayedCount(current);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        animationFrameRef.current = null;
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [spanCounts?.filtered, displayedCount]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <Stack gap={0} mr="auto" data-testid="span-count-container">
      <Flex align="center" gap="xxs">
        <Tooltip
          label={intl.formatMessage(
            { id: 'filter.spanCountTooltip' },
            { count: capAndFormatNumber(displayedCount) }
          )}
          withArrow
          openDelay={500}
          data-testid="span-count-tooltip"
        >
          {isFetching ? (
            <Skeleton
              width={30}
              height={24}
              radius="sm"
              data-testid="span-count-skeleton"
            />
          ) : (
            <Text fz="md" fw={500} data-testid="span-count-value">
              {capAndFormatNumber(displayedCount)}
            </Text>
          )}
        </Tooltip>
        <Text fz="md" fw={500}>
          {intl.formatMessage({ id: 'noun.spans' })}
        </Text>
      </Flex>
      <Flex align="center" gap="xxs">
        <Tooltip
          label={intl.formatMessage({ id: 'filter.totalSpansTooltip' })}
          withArrow
          openDelay={500}
        >
          <Text c="grayBrand.5" fz="xs" fw={400}>
            {intl.formatMessage({ id: 'filter.of' })}{' '}
            {capAndFormatNumber(spanCounts?.total || 0)}
          </Text>
        </Tooltip>
      </Flex>
    </Stack>
  );
}
